import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./testimonials.css";
import { Sparkles, MapPin, Star } from "lucide-react";

const testimonials = [
  {
    id: 1,
    name: "Amit Gupta",
    role: "CEO, TechVision India",
    text: "Working with EduTalent was a turning point for our organisation. Their corporate training programs were surgically tailored to our challenges — within 90 days we saw measurable improvements in team output and leadership confidence across all levels.",
    rating: 5,
    avatar: "AG",
    color: "#7c3aed",
    bg: "rgba(124,58,237,0.12)",
    country: "India",
    service: "Corporate Training",
  },
  {
    id: 2,
    name: "Sarah Mitchell",
    role: "HR Director, Global Exports Ltd",
    text: "The talent acquisition team didn't just send CVs — they understood our culture, our pace, and the kind of people who would thrive here. Every candidate they put forward was genuinely impressive. We've made 12 hires through them and haven't looked back.",
    rating: 5,
    avatar: "SM",
    color: "#db2777",
    bg: "rgba(219,39,119,0.12)",
    country: "UK",
    service: "Talent Acquisition",
  },
  {
    id: 3,
    name: "Vikram Nair",
    role: "Founder, StartUp Hub",
    text: "We were at a crossroads — scale fast or consolidate. EduTalent's consultants helped us see clearly, prioritise ruthlessly, and execute confidently. Six months later, we doubled revenue and expanded to two new cities. I can't recommend them highly enough.",
    rating: 5,
    avatar: "VN",
    color: "#059669",
    bg: "rgba(5,150,105,0.12)",
    country: "India",
    service: "Business Consulting",
  },
  {
    id: 4,
    name: "Meera Patel",
    role: "Operations Manager, LogiServe",
    text: "Our legacy systems were slowing everything down. EduTalent mapped our entire digital transformation journey, from cloud migration to workflow automation. The transition was smooth, the team was patient, and the results speak for themselves.",
    rating: 5,
    avatar: "MP",
    color: "#d97706",
    bg: "rgba(217,119,6,0.12)",
    country: "India",
    service: "Digital Transformation",
  },
  {
    id: 5,
    name: "James Anderson",
    role: "MD, Gulf Investments",
    text: "Finding a firm that truly understands the UAE market is rare. EduTalent's Globe team brought local insight, senior-level relationships, and polished execution. They placed three key leaders for us and each has been a revelation.",
    rating: 5,
    avatar: "JA",
    color: "#2563eb",
    bg: "rgba(37,99,235,0.12)",
    country: "UAE",
    service: "Leadership Development",
  },
  {
    id: 6,
    name: "Pooja Singh",
    role: "Senior Product Manager",
    text: "I was stuck in the same role for four years, unsure how to pivot. My EduTalent coach helped me reframe my story, rebuild my personal brand, and interview with confidence. Three months later I landed a role I didn't think was possible. Truly life-changing.",
    rating: 5,
    avatar: "PS",
    color: "#7c3aed",
    bg: "rgba(124,58,237,0.12)",
    country: "India",
    service: "Career Coaching",
  },
];

const stats = [
  { value: "500+", label: "Happy Clients" },
  { value: "98%", label: "Would Recommend" },
  { value: "15+", label: "Years of Trust" },
  { value: "2", label: "Countries Served" },
];

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const isStandalone = window.location.pathname === "/testimonials";
  const perPage = 3;
  const total = testimonials.length;

  const goTo = (idx) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => { setActiveIndex(idx); setAnimating(false); }, 260);
  };

  const prev = () => { if (activeIndex > 0) goTo(activeIndex - 1); };
  const next = () => { if (activeIndex < total - perPage) goTo(activeIndex + 1); };

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((i) => (i >= total - perPage ? 0 : i + 1));
    }, 5000);
    return () => clearInterval(timer);
  }, [total, perPage]);

  const visible = testimonials.slice(activeIndex, activeIndex + perPage);

  return (
    <section id="testimonials" className={`tst-section ${isStandalone ? 'standalone-page' : ''}`}>

      {/* Backgrounds */}
      <div className="tst-bg-photo">
        <img src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1400" alt="" />
        <div className="tst-bg-dim"></div>
      </div>
      <div className="tst-radials"></div>
      <div className="tst-blob tst-blob-1"></div>
      <div className="tst-blob tst-blob-2"></div>
      <div className="tst-blob tst-blob-3"></div>

      {/* Decorative quote marks */}
      <div className="tst-deco-q tst-deco-q1">"</div>
      <div className="tst-deco-q tst-deco-q2">"</div>

      <div className="container tst-inner">

        {/* Header */}
        <div className="tst-header">
          <span className="tst-badge" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <Sparkles size={14} /> Client Stories
          </span>
          <h2 className="tst-title">Trusted by <span className="tst-accent">Leaders Across Industries</span></h2>
          <p className="tst-sub">Real words from the businesses and professionals we've had the privilege of working with.</p>
          <div className="tst-hdr-line"></div>
        </div>

        {/* Stats strip */}
        <div className="tst-stats">
          {stats.map((s, i) => (
            <div key={i} className="tst-stat">
              <h3 className="tst-stat-val">{s.value}</h3>
              <p className="tst-stat-lbl">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Cards */}
        <div className={`tst-grid${animating ? " tst-grid--fade" : ""}`}>
          {visible.map((t, i) => (
            <div
              key={t.id}
              className="tst-card"
              style={{ "--tc": t.color, "--tb": t.bg, animationDelay: `${i * 100}ms` }}
            >
              {/* Orb glow */}
              <div className="tst-card-orb"></div>

              {/* Top: quote + service tag */}
              <div className="tst-card-top">
                <span className="tst-quote-icon">"</span>
                <span className="tst-service-tag">{t.service}</span>
              </div>

              {/* Stars */}
              <div className="tst-stars" style={{ display: "flex", gap: "2px" }}>
                {[...Array(t.rating)].map((_, j) => (
                  <span key={j} className="tst-star" style={{ color: "#f7c948" }}>
                    <Star size={14} fill="currentColor" stroke="none" />
                  </span>
                ))}
              </div>

              {/* Text */}
              <p className="tst-text">{t.text}</p>

              {/* Divider */}
              <div className="tst-divider"></div>

              {/* Author */}
              <div className="tst-author">
                <div className="tst-avatar">
                  <span>{t.avatar}</span>
                  <div className="tst-avatar-ring"></div>
                </div>
                <div className="tst-author-info">
                  <strong className="tst-name">{t.name}</strong>
                  <span className="tst-role">{t.role}</span>
                  <span className="tst-country" style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    <MapPin size={12} /> {t.country}
                  </span>
                </div>
              </div>

              {/* Bottom accent */}
              <div className="tst-card-line"></div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="tst-nav">
          <button onClick={prev} disabled={activeIndex === 0} className="tst-nav-btn" aria-label="Previous">
            ← Prev
          </button>

          <div className="tst-dots">
            {testimonials.map((_, i) => (
              <button
                key={i}
                className={`tst-dot${i === activeIndex ? " tst-dot--active" : ""}`}
                onClick={() => goTo(Math.min(i, total - perPage))}
                aria-label={`Go to ${i + 1}`}
              />
            ))}
          </div>

          <button onClick={next} disabled={activeIndex >= total - perPage} className="tst-nav-btn" aria-label="Next">
            Next →
          </button>
        </div>

        {/* Bottom CTA */}
        <div className="tst-cta">
          <div className="tst-cta-bg">
            <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200" alt="" />
            <div className="tst-cta-dim"></div>
          </div>
          <div className="tst-cta-inner">
            <div className="tst-cta-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#c9a84c" }}>
              <Sparkles size={32} />
            </div>
            <div>
              <h3 className="tst-cta-h">Ready to write your own success story?</h3>
              <p className="tst-cta-p">Join 500+ businesses and professionals who transformed their growth with EduTalent.</p>
            </div>
            <div className="tst-cta-btns">
              <Link to="/contact" className="tst-btn-w">Get Started →</Link>
              <Link to="/services" className="tst-btn-g">View Services</Link>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Testimonials;
